import { forwardRef } from 'react';
import styled, { keyframes } from 'styled-components';

import { FLOATING_TOOLBAR_HEIGHT } from './useFloatingToolbarPosition';

import type { FloatingToolbarPosition } from './useFloatingToolbarPosition';
import type { ReactNode } from 'react';

interface FloatingToolbarSurfaceProps {
  pos: FloatingToolbarPosition | null;
  children: ReactNode;
}

/**
 * Canvas-anchored floating toolbar shell. Renders in place — inside Stage's
 * stacking context, like TextFloatingToolbar since PLAT-11264, never a body
 * portal: that keeps the AR / Fit dropdowns above it and keeps it visible in
 * fullscreen. Fades in. When `pos` is null it parks the toolbar off-screen
 * and hidden — keeping it mounted so its width can be measured for centering
 * before the first paint.
 *
 * Visual style mirrors TextFloatingToolbar so all element toolbars match.
 */
export const FloatingToolbarSurface = forwardRef<
  HTMLDivElement,
  FloatingToolbarSurfaceProps
>(({ pos, children }, ref) => {
  const style = pos
    ? { left: pos.x, top: pos.y }
    : { left: -9999, top: -9999, visibility: 'hidden' as const };

  return (
    <SToolbar ref={ref} style={style}>
      {children}
    </SToolbar>
  );
});

FloatingToolbarSurface.displayName = 'FloatingToolbarSurface';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(4px); }
  to   { opacity: 1; transform: scale(1)    translateY(0);   }
`;

const SToolbar = styled.div`
  position: fixed;
  /* Deliberately small, the same rank as TextFloatingToolbar: above the
     canvas, below FitDropdown (10000) and AspectRatiosDropdown (1000008) so
     those open over it. */
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 2px;
  height: ${FLOATING_TOOLBAR_HEIGHT}px;
  padding: 0 10px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.35),
    0 1px 3px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 140ms ease-out both;
  pointer-events: all;
  user-select: none;
  white-space: nowrap;
`;

export const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: var(--border);
  margin: 0 4px;
  flex-shrink: 0;
`;

/**
 * Square icon button. `isActive` paints the accent-tinted "on" state used by
 * toggles; omit it for plain actions like Delete / More.
 */
export const ToolbarIconButton = styled.button<{ isActive?: boolean }>`
  position: relative;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isActive }) =>
    isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'};
  border: ${({ isActive }) =>
    isActive ? '1px solid var(--accent)' : '1px solid transparent'};
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background 100ms ease,
    border-color 100ms ease;
  &:hover {
    background: ${({ isActive }) =>
      isActive
        ? 'color-mix(in srgb, var(--accent) 22%, transparent)'
        : 'var(--surface-elevated)'};
  }
  &:active {
    background: var(--border);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

/**
 * Compact ± button used by inline steppers (font size, zoom). Pair with
 * useHoldRepeat for press-and-hold repeat.
 */
export const ToolbarStepButton = styled.button`
  width: 24px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &:active:not(:disabled) {
    background: var(--border);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    color: var(--text-muted);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

/**
 * Dropdown surface for toolbar value pickers (Opacity, Volume). Rendered as a
 * sibling of the toolbar, never a child: SToolbar's fade-in ends in a
 * transform, which would trap a fixed descendant. Position it with a
 * `style={{ top, left }}` from the trigger's rect.
 */
export const ToolbarMenu = styled.div`
  position: fixed;
  z-index: 101;
  width: 96px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 6px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 2px 6px rgba(0, 0, 0, 0.2);
`;

export const ToolbarMenuItem = styled.button<{ isSelected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: ${({ isSelected }) =>
    isSelected ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'none'};
  color: ${({ isSelected }) =>
    isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  font-size: var(--text-sm);
  cursor: pointer;
  &:hover {
    background: ${({ isSelected }) =>
      isSelected
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

/**
 * Text/label button used for compact dropdowns (e.g. the Opacity value) and
 * segmented options (Fit). `isActive` reflects selected / open state.
 */
export const ToolbarTextButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: ${({ isActive }) =>
    isActive ? 'var(--accent)' : 'var(--text-secondary)'};
  background: ${({ isActive }) =>
    isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'};
  border: 1px solid
    ${({ isActive }) => (isActive ? 'var(--accent)' : 'transparent')};
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 100ms ease,
    border-color 100ms ease;
  &:hover {
    background: var(--surface-elevated);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;
